import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import ReactDOMServer from 'react-dom/server';
import html2pdf from 'html2pdf.js';
import './styles/Marklive.css';
import { FaRegClone, FaSave, FaFilePdf } from 'react-icons/fa';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const TOOLBAR_BTNS = [
  { label: 'H1', action: 'h1', title: 'Tiêu đề lớn' },
  { label: 'H2', action: 'h2', title: 'Tiêu đề vừa' },
  { label: 'H3', action: 'h3', title: 'Tiêu đề nhỏ' },
  { label: 'B', action: 'bold', title: 'In đậm' },
  { label: 'I', action: 'italic', title: 'In nghiêng' },
  { label: 'S', action: 'strike', title: 'Gạch ngang' },
  { label: 'Link', action: 'link', title: 'Chèn liên kết' },
  { label: 'Image', action: 'image', title: 'Chèn ảnh' },
  { label: 'List', action: 'list', title: 'Tạo danh sách' },
];

const Marklive = () => {
  const [markdown, setMarkdown] = useState(() => {
    // Load from localStorage nếu có
    return localStorage.getItem('marklive_markdown') || `# Welcome to Marklive!\n## Edit on the right -> see markdown on the left.\n\n| Feature | Status |\n|---|---|\n| Direct Editing | ✅ |\n| Line Breaks (Enter) | ✅ |\n| Stability | ✅ |\n| Data 1<br>Data 2 | Data 3 |`;
  });

  const textareaRef = useRef(null);
  const previewRef = useRef(null); // Ref for the editable preview div
  const turndownService = useRef(null);
  const [isEditorOpen, setIsEditorOpen] = useState(true);

  // Initialize Turndown Service
  useEffect(() => {
    const tdService = new TurndownService({
      headingStyle: 'atx',
      hr: '---',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
      emDelimiter: '*',
    });
    tdService.use(gfm);
    tdService.addRule('keep-br', {
        filter: 'br',
        replacement: () => '<br>'
    });
    turndownService.current = tdService;
  }, []);
  
  const handleMarkdownChange = (e) => {
    setMarkdown(e.target.value);
  };

  const handlePreviewBlur = () => {
    if (previewRef.current && turndownService.current) {
      const html = previewRef.current.innerHTML;
      const newMarkdown = turndownService.current.turndown(html);
      setMarkdown(newMarkdown);
    }
  };

  const handlePreviewKeyDown = (e) => {
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      let currentNode = range.startContainer;
      
      if (currentNode.nodeType === Node.TEXT_NODE) {
        currentNode = currentNode.parentElement;
      }

      const tableCell = currentNode && currentNode.closest ? currentNode.closest('td, th') : null;

      if (tableCell) {
        e.preventDefault();
        e.stopPropagation();

        const range = selection.getRangeAt(0);
        range.deleteContents(); // Clear any selected text

        const br = document.createElement('br');
        range.insertNode(br);

        // Move cursor immediately after the inserted <br>
        const newRange = document.createRange();
        newRange.setStartAfter(br);
        newRange.collapse(true);

        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  };
  
  const handleToolbar = (action) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    let newText = markdown;
    let before = markdown.slice(0, start);
    let selected = markdown.slice(start, end);
    let after = markdown.slice(end);

    switch (action) {
      case 'h1':
        newText = before + `# ${selected || 'Tiêu đề lớn'}\n` + after;
        break;
      case 'h2':
        newText = before + `## ${selected || 'Tiêu đề vừa'}\n` + after;
        break;
      case 'h3':
        newText = before + `### ${selected || 'Tiêu đề nhỏ'}\n` + after;
        break;
      case 'bold':
        newText = before + `**${selected || 'in đậm'}**` + after;
        break;
      case 'italic':
        newText = before + `*${selected || 'in nghiêng'}*` + after;
        break;
      case 'strike':
        newText = before + `~~${selected || 'gạch ngang'}~~` + after;
        break;
      case 'link':
        newText = before + `[${selected || 'tiêu đề link'}](${prompt('Nhập URL liên kết:', 'https://') || ''})` + after;
        break;
      case 'image':
        newText = before + `![${selected || 'mô tả ảnh'}](${prompt('Nhập URL ảnh:', 'https://') || ''})` + after;
        break;
      case 'list':
        // Đảm bảo luôn có dòng trống phía trước danh sách
        const prefix = (before.endsWith('\n') || before.length === 0) ? '' : '\n';
        newText = before + prefix + `- ${selected || 'Mục danh sách'}\n` + after;
        break;
      default:
        break;
    }
    setMarkdown(newText);
  };

  // Hàm copy toàn bộ markdown
  const handleCopyAll = () => {
    navigator.clipboard.writeText(markdown);
  };
  // Hàm lưu tạm vào localStorage
  const handleSaveTemp = () => {
    localStorage.setItem('marklive_markdown', markdown);
  };

  // Hàm export PDF từ preview
  const handleExportPDF = () => {
    if (!previewRef.current) return;

    // Tạo một bản sao của preview content để tùy chỉnh cho PDF
    const previewContent = previewRef.current.cloneNode(true);
    
    // Tạo container cho PDF với styling phù hợp
    const pdfContainer = document.createElement('div');
    pdfContainer.style.padding = '40px';
    pdfContainer.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    pdfContainer.style.fontSize = '15px';
    pdfContainer.style.lineHeight = '1.6';
    pdfContainer.style.color = '#333';
    pdfContainer.style.maxWidth = '800px';
    pdfContainer.style.margin = '0 auto';
    pdfContainer.style.backgroundColor = '#fff';
    
    // Copy nội dung từ preview
    pdfContainer.innerHTML = previewContent.innerHTML;
    
    // Xử lý links để hiển thị URL trong PDF
    const links = pdfContainer.querySelectorAll('a');
    links.forEach(link => {
      const url = link.getAttribute('href');
      const text = link.textContent;
      if (url && url !== text) {
        link.textContent = `${text} (${url})`;
      }
    });
    
    // Xử lý images để hiển thị alt text và URL
    const images = pdfContainer.querySelectorAll('img');
    images.forEach(img => {
      const src = img.getAttribute('src');
      const alt = img.getAttribute('alt') || 'Image';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.border = '1px solid #ddd';
      img.style.padding = '5px';
      img.style.margin = '10px 0';
      // Thêm caption cho image
      const caption = document.createElement('div');
      caption.textContent = `${alt} - ${src}`;
      caption.style.fontSize = '0.8em';
      caption.style.color = '#666';
      caption.style.textAlign = 'center';
      caption.style.marginTop = '5px';
      img.parentNode.insertBefore(caption, img.nextSibling);
    });
    
    // Xử lý code blocks để đảm bảo font monospace và wrapping
    const codeBlocks = pdfContainer.querySelectorAll('pre code');
    codeBlocks.forEach(code => {
      code.style.fontFamily = 'Courier New, monospace';
      code.style.fontSize = '0.9em';
      code.style.whiteSpace = 'pre-wrap';
      code.style.wordWrap = 'break-word';
    });
    
    // Xử lý inline code
    const inlineCodes = pdfContainer.querySelectorAll('code:not(pre code)');
    inlineCodes.forEach(code => {
      code.style.fontFamily = 'Courier New, monospace';
      code.style.fontSize = '0.9em';
    });
    
    // Thêm CSS styling cho các element trong PDF
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Headings */
      h1, h2, h3, h4, h5, h6 {
        margin-top: 1.2em;
        margin-bottom: 0.6em;
        font-weight: 600;
        color: #111;
      }
      h1 { 
        font-size: 1.8em; 
        border-bottom: 1px solid #ddd; 
        padding-bottom: 0.3em; 
      }
      h2 { 
        font-size: 1.4em; 
        border-bottom: 1px solid #eee; 
        padding-bottom: 0.3em; 
      }
      h3 { 
        font-size: 1.2em; 
      }
      
      /* Paragraphs and text */
      p { 
        margin: 1em 0; 
      }
      
      /* Links */
      a { 
        color: #007bff; 
        text-decoration: none; 
      }
      
      /* Lists */
      ul, ol { 
        padding-left: 1.5em; 
      }
      li { 
        margin-bottom: 0.4em; 
      }
      
      /* Blockquotes */
      blockquote {
        border-left: 3px solid #ccc;
        margin: 1em 0;
        padding: 0.2em 1em;
        background-color: #f8f8f8;
        color: #666;
      }
      
      /* Code */
      code {
        background-color: #eee;
        padding: 2px 5px;
        border-radius: 4px;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 0.9em;
      }
      
      /* Code blocks */
      pre {
        background-color: #f3f3f3;
        padding: 1em;
        border-radius: 4px;
        overflow-x: auto;
        margin: 1em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }
      
      /* Tables */
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 1em 0;
        border: 1px solid #ddd;
      }
      th, td {
        border: 1px solid #ddd;
        padding: 8px 12px;
        text-align: left;
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
        vertical-align: top !important;
      }
      th {
        background-color: #f2f2f2;
        font-weight: 600;
      }
      tr:nth-child(even) {
        background-color: #f9f9f9;
      }
    `;
    
    // Thêm style vào container
    pdfContainer.appendChild(styleElement);
    
    // Cấu hình PDF options
    const pdfOptions = {
      margin: [15, 15, 15, 15],
      filename: `marklive-export-${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      }
    };

    // Export PDF
    html2pdf()
      .from(pdfContainer)
      .set(pdfOptions)
      .save()
      .catch(error => {
        console.error('Lỗi khi export PDF:', error);
        alert('Có lỗi xảy ra khi export PDF. Vui lòng thử lại.');
      });
  };

  // Khi markdown thay đổi, nếu đã từng lưu thì tự động nạp lại khi load trang
  useEffect(() => {
    // Đã xử lý ở useState khởi tạo
  }, []);

  return (
    <div className="marklive-container">
      <div className="marklive-editor">
        {/* Markdown Editor Panel */}
        {isEditorOpen && (
          <div className="editor-panel">
            <div className="panel-header">
              <h3>Markdown Editor</h3>
            </div>
            <div className="toolbar">
              {TOOLBAR_BTNS.map(btn => (
                <button
                  key={btn.action}
                  className="toolbar-btn"
                  title={btn.title}
                  onClick={() => handleToolbar(btn.action)}
                  type="button"
                >
                  {btn.label}
                </button>
              ))}
              <button
                className="toolbar-btn"
                title="Copy All"
                style={{ marginLeft: 'auto' }}
                onClick={handleCopyAll}
                type="button"
              >
                <FaRegClone />
              </button>
              <button
                className="toolbar-btn"
                title="Lưu tạm vào trình duyệt"
                onClick={handleSaveTemp}
                type="button"
              >
                <FaSave />
              </button>
              <button
                className="toolbar-btn"
                title="Export Preview ra PDF"
                onClick={handleExportPDF}
                type="button"
              >
                <FaFilePdf data-icon="file-pdf" />
              </button>
            </div>
            <textarea
              className="markdown-editor"
              value={markdown}
              onChange={handleMarkdownChange}
              placeholder="Write your markdown here..."
              ref={textareaRef}
            />
          </div>
        )}

        {/* Preview Panel */}
        <div className={`preview-panel ${!isEditorOpen ? 'full-width' : ''}`}>
          <div className="panel-header">
            <h3>Preview (Click to Edit)</h3>
            <button
              className={`toggle-editor-btn ${isEditorOpen ? 'close' : 'open'}`}
              onClick={() => setIsEditorOpen(!isEditorOpen)}
              title={isEditorOpen ? "Ẩn editor để xem preview rộng hơn" : "Hiện editor để chỉnh sửa"}
              type="button"
            >
              {isEditorOpen ? (
                <>
                  <FaChevronLeft />
                  <span>Ẩn Editor</span>
                </>
              ) : (
                <>
                  <FaChevronRight />
                  <span>Hiện Editor</span>
                </>
              )}
            </button>
          </div>
          <div
            className="markdown-preview"
            ref={previewRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            onBlur={handlePreviewBlur}
            onKeyDown={handlePreviewKeyDown}
            // Dangerously set inner HTML from a reliable source (ReactMarkdown's output)
            // This decouples React's VDOM from the contentEditable DOM, preventing errors.
            dangerouslySetInnerHTML={{
              __html: ReactDOMServer.renderToString(
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {markdown}
                </ReactMarkdown>
              )
            }}
          >
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marklive; 